import React, { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS service（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import ConfirmModal from "../../components/modal/ConfirmModal";
import Toggle from "../../components/form-item/Toggle";
import StatusBadge from "../../components/status-badge/StatusBadge";
import SettingPresetModal from "./components/SettingPresetModal";
import ScenePresetModal from "./components/ScenePresetModal";
import {
  AI_PROVIDER_LABELS,
  AiScene,
  AiScenePreset,
  AiScenePresetInput,
  AiSettingPreset,
  AiSettingPresetInput,
  useActivateScenePresetMutation,
  useActivateSettingPresetMutation,
  useTestSettingPresetMutation,
  useCreateScenePresetMutation,
  useCreateSettingPresetMutation,
  useDeleteScenePresetMutation,
  useDeleteSettingPresetMutation,
  useGetAiOverviewQuery,
  useUpdateAiSettingMutation,
  useUpdateScenePresetMutation,
  useUpdateSettingPresetMutation,
} from "../../app/services/adminAi";

const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap24",
})`
  padding: 24px 16px 80px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Section = styled.section.attrs({
  className: "u-col u-w100 u-gap12",
})``;

const Card = styled.div.attrs({
  className: "u-col u-w100",
})`
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  overflow: hidden;
`;

const PresetRow = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 12px;
  background: ${({ $active }) =>
    $active ? "var(--soremo-bg-blue, rgba(0, 154, 206, 0.06))" : "transparent"};
  border-bottom: 1px solid var(--soremo-border);

  &:last-child {
    border-bottom: none;
  }
`;

const SceneHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--soremo-background, #fafafa);
  }
`;

const RowActions = styled.div.attrs({
  className: "u-row u-items-center u-gap8",
})`
  flex-shrink: 0;
`;

// `.u-p*`（全方向 padding）の utility は未定義のため局所 styled で対応。
const CardBody = styled.div.attrs({
  className: "u-row-between u-items-center u-w100",
})`
  padding: 12px;
`;

/** RTK Query のエラーから BE の message を取り出す（無ければ fallback）。 */
const extractErrorMessage = (err: unknown, fallback: string): string =>
  (err as { data?: { message?: string } })?.data?.message ?? fallback;

type SettingModalState =
  | { mode: "create" }
  | { mode: "edit"; preset: AiSettingPreset }
  | { mode: "duplicate"; preset: AiSettingPreset }
  | null;

type SceneModalState = {
  scene: AiScene;
  preset: AiScenePreset | null;
} | null;

type ConfirmState = {
  title: string;
  description: string;
  onConfirm: () => void;
} | null;

/**
 * 管理者向け AI 設定ページ（`/admin/ai`）。
 *
 * AI テキスト生成（チクチクフィルター / 返信君 / レビュー生成など）の設定を
 * 「名前付きプリセット」として複数保存し、アクティブなものを切り替える。
 *
 * - 上部: AI 機能全体の有効 / 無効トグル
 * - セクション 1: AI モデル設定プリセット（プロバイダ / モデル / パラメータ）
 *   全体で 1 件だけアクティブにできる。アクティブが無い場合は Django admin の
 *   AI 設定（シングルトン）が使われる。
 * - セクション 2: シーン別プロンプト。シーンごとに複数のプロンプト案を保存し、
 *   シーン内で 1 件だけアクティブにできる。アクティブが無い場合は従来の
 *   AI シーン設定（Django admin）→ コードデフォルトの順で使われる。
 *
 * 権限は `role === "ADMIN"`（master_admin）のみ。
 */
const AdminAiSettings: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();
  const role: string | undefined = currentUser?.data?.role;

  const {
    data: overview,
    isLoading: isOverviewLoading,
    isFetching: isOverviewFetching,
  } = useGetAiOverviewQuery(undefined, { skip: role !== "ADMIN" });

  const [updateAiSetting, { isLoading: isTogglingEnabled }] =
    useUpdateAiSettingMutation();
  const [createSettingPreset, { isLoading: isCreatingSetting }] =
    useCreateSettingPresetMutation();
  const [updateSettingPreset, { isLoading: isUpdatingSetting }] =
    useUpdateSettingPresetMutation();
  const [deleteSettingPreset] = useDeleteSettingPresetMutation();
  const [activateSettingPreset] = useActivateSettingPresetMutation();
  const [testSettingPreset] = useTestSettingPresetMutation();
  const [testingPresetId, setTestingPresetId] = useState<number | null>(null);
  const [createScenePreset, { isLoading: isCreatingScene }] =
    useCreateScenePresetMutation();
  const [updateScenePreset, { isLoading: isUpdatingScene }] =
    useUpdateScenePresetMutation();
  const [deleteScenePreset] = useDeleteScenePresetMutation();
  const [activateScenePreset] = useActivateScenePresetMutation();

  const [settingModal, setSettingModal] = useState<SettingModalState>(null);
  const [sceneModal, setSceneModal] = useState<SceneModalState>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [expandedScenes, setExpandedScenes] = useState<Set<string>>(
    () => new Set(),
  );

  const isEnabled = overview?.data.is_enabled ?? false;
  const settingPresets = overview?.data.setting_presets ?? [];
  const scenes = overview?.data.scenes ?? [];
  // kind 未対応の旧レスポンスは text 扱い。
  const textScenes = scenes.filter((s) => (s.kind ?? "text") !== "image");
  const imageScenes = scenes.filter((s) => s.kind === "image");

  // ===== 全体トグル =====
  const handleToggleEnabled = useCallback(async () => {
    if (isTogglingEnabled || !overview) return;
    const next = !overview.data.is_enabled;
    try {
      await updateAiSetting({ is_enabled: next }).unwrap();
      toast.success(
        next ? "AI 生成機能を有効にしました" : "AI 生成機能を無効にしました",
      );
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err, "設定の更新に失敗しました"));
    }
  }, [isTogglingEnabled, overview, updateAiSetting]);

  // ===== グローバル設定プリセット =====
  const handleSettingSubmit = useCallback(
    async (body: AiSettingPresetInput) => {
      if (!settingModal) return;
      try {
        if (settingModal.mode === "edit") {
          await updateSettingPreset({
            id: settingModal.preset.id,
            body,
          }).unwrap();
          toast.success("プリセットを保存しました");
        } else {
          await createSettingPreset(body).unwrap();
          toast.success("プリセットを作成しました");
        }
        setSettingModal(null);
      } catch (err: unknown) {
        toast.error(extractErrorMessage(err, "プリセットの保存に失敗しました"));
      }
    },
    [settingModal, createSettingPreset, updateSettingPreset],
  );

  const handleSettingActivate = useCallback(
    async (preset: AiSettingPreset, active: boolean) => {
      try {
        await activateSettingPreset({ id: preset.id, active }).unwrap();
        toast.success(
          active
            ? `「${preset.name}」を有効にしました`
            : `「${preset.name}」を解除しました（Django admin の AI 設定に戻ります）`,
        );
      } catch (err: unknown) {
        toast.error(extractErrorMessage(err, "切り替えに失敗しました"));
      }
    },
    [activateSettingPreset],
  );

  // 保存済みの設定で 1 回だけ生成を実行し、使える組み合わせかを確かめる。
  const handleSettingTest = useCallback(
    async (preset: AiSettingPreset) => {
      setTestingPresetId(preset.id);
      try {
        const { data } = await testSettingPreset(preset.id).unwrap();
        const elapsed = `${(data.elapsed_ms / 1000).toFixed(1)} 秒`;
        if (data.ok) {
          toast.success(`「${preset.name}」: ${data.message}（${elapsed}）`);
        } else {
          toast.error(`「${preset.name}」: ${data.message}`, { duration: 8000 });
        }
      } catch (err: unknown) {
        toast.error(extractErrorMessage(err, "接続テストを実行できませんでした"));
      } finally {
        setTestingPresetId(null);
      }
    },
    [testSettingPreset],
  );

  const handleSettingDelete = useCallback(
    (preset: AiSettingPreset) => {
      setConfirm({
        title: "プリセットを削除",
        description: `「${preset.name}」を削除します。この操作は取り消せません。`,
        onConfirm: async () => {
          try {
            await deleteSettingPreset(preset.id).unwrap();
            toast.success("プリセットを削除しました");
          } catch (err: unknown) {
            toast.error(
              extractErrorMessage(err, "プリセットの削除に失敗しました"),
            );
          }
          setConfirm(null);
        },
      });
    },
    [deleteSettingPreset],
  );

  // ===== シーン別プロンプトプリセット =====
  const toggleSceneExpanded = useCallback((key: string) => {
    setExpandedScenes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleSceneSubmit = useCallback(
    async (body: AiScenePresetInput) => {
      if (!sceneModal) return;
      try {
        if (sceneModal.preset) {
          await updateScenePreset({ id: sceneModal.preset.id, body }).unwrap();
          toast.success("プロンプト案を保存しました");
        } else {
          await createScenePreset({
            sceneKey: sceneModal.scene.key,
            body,
          }).unwrap();
          toast.success("プロンプト案を作成しました");
        }
        setSceneModal(null);
      } catch (err: unknown) {
        toast.error(
          extractErrorMessage(err, "プロンプト案の保存に失敗しました"),
        );
      }
    },
    [sceneModal, createScenePreset, updateScenePreset],
  );

  const handleSceneActivate = useCallback(
    async (preset: AiScenePreset, active: boolean) => {
      try {
        await activateScenePreset({ id: preset.id, active }).unwrap();
        toast.success(
          active
            ? `「${preset.name}」を有効にしました`
            : `「${preset.name}」を解除しました（デフォルト文面に戻ります）`,
        );
      } catch (err: unknown) {
        toast.error(extractErrorMessage(err, "切り替えに失敗しました"));
      }
    },
    [activateScenePreset],
  );

  const handleSceneDelete = useCallback(
    (preset: AiScenePreset) => {
      setConfirm({
        title: "プロンプト案を削除",
        description: `「${preset.name}」を削除します。この操作は取り消せません。`,
        onConfirm: async () => {
          try {
            await deleteScenePreset(preset.id).unwrap();
            toast.success("プロンプト案を削除しました");
          } catch (err: unknown) {
            toast.error(
              extractErrorMessage(err, "プロンプト案の削除に失敗しました"),
            );
          }
          setConfirm(null);
        },
      });
    },
    [deleteScenePreset],
  );

  // ===== 権限ガード =====
  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }
  if (role !== "ADMIN") {
    return <Navigate to="/projects" replace />;
  }

  // シーンカード（テキスト / 画像共通）。
  const renderSceneCard = (scene: AiScene) => {
    const isExpanded = expandedScenes.has(scene.key);
    const activePreset = scene.presets.find((p) => p.is_active) ?? null;
    const isImageScene = scene.kind === "image";
    return (
      <Card key={scene.key}>
        <SceneHeader
          type="button"
          onClick={() => toggleSceneExpanded(scene.key)}
        >
          <div className="u-col u-fill u-gap2">
            <div className="u-row u-items-center u-gap8">
              <span className="heading-13">
                {scene.display_name}
              </span>
              {!scene.is_enabled && (
                <StatusBadge
                  label="シーン無効"
                  variant="chip"
                  color="light-gray"
                  size="small"
                />
              )}
            </div>
            <span className="bodytext-11 u-text-light-gray">
              {scene.key} / 使用中:{" "}
              {activePreset ? `「${activePreset.name}」` : "デフォルト"}
            </span>
          </div>
          <span
            className="material-symbols-rounded u-text-light-gray"
            aria-hidden
          >
            {isExpanded ? "expand_less" : "expand_more"}
          </span>
        </SceneHeader>
        {isExpanded && (
          <div className="u-col u-w100">
            {scene.presets.map((preset) => (
              <PresetRow key={preset.id} $active={preset.is_active}>
                <div className="u-col u-fill u-gap2">
                  <div className="u-row u-items-center u-gap8">
                    <span className="bodytext-13">
                      {preset.name}
                    </span>
                    {preset.is_active && (
                      <StatusBadge
                        label="アクティブ"
                        variant="chip"
                        color="blue"
                        size="small"
                        leftIcon="check_circle"
                      />
                    )}
                  </div>
                  <span className="bodytext-11 u-text-light-gray">
                    {preset.system_prompt
                      ? `${preset.system_prompt.slice(0, 60)}${preset.system_prompt.length > 60 ? "…" : ""}`
                      : "システムプロンプト: デフォルトを使用"}
                    {!isImageScene && ` / 履歴 ${preset.history_limit} 件`}
                  </span>
                </div>
                <RowActions>
                  {preset.is_active ? (
                    <Button
                      size="small"
                      theme="tertiary"
                      variant="text"
                      onClick={() =>
                        void handleSceneActivate(preset, false)
                      }
                    >
                      解除
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      theme="primary"
                      variant="outlined"
                      onClick={() =>
                        void handleSceneActivate(preset, true)
                      }
                    >
                      有効にする
                    </Button>
                  )}
                  <IconButton
                    icon="edit"
                    size={20}
                    title="編集"
                    ariaLabel="プロンプト案を編集"
                    onClick={() =>
                      setSceneModal({ scene, preset })
                    }
                  />
                  <IconButton
                    icon="delete"
                    size={20}
                    title={
                      preset.is_active
                        ? "アクティブな案は削除できません"
                        : "削除"
                    }
                    ariaLabel="プロンプト案を削除"
                    onClick={() => handleSceneDelete(preset)}
                    disabled={preset.is_active}
                  />
                </RowActions>
              </PresetRow>
            ))}
            <CardBody>
              <Button
                size="small"
                theme="tertiary"
                variant="outlined"
                leftIcon="add"
                onClick={() =>
                  setSceneModal({ scene, preset: null })
                }
              >
                プロンプト案を追加
              </Button>
            </CardBody>
          </div>
        )}
      </Card>
    );
  };

  return (
    <PageWrapper>
      <TitleBlock
        title="AI 設定"
        description="チクチクフィルター・返信君・レビュー生成などの AI テキスト生成と、アバター・バナー・作品サムネの AI 画像生成の設定を管理します。モデル設定とシーン別プロンプトを名前付きプリセットとして複数保存し、アクティブなものを切り替えられます。"
      />

      {isOverviewLoading ? (
        <SvgLoadingAnimation />
      ) : (
        <>
          {/* ===== 全体トグル ===== */}
          <Section>
            <Card>
              <CardBody>
                <Toggle
                  value={isEnabled}
                  onClick={() => void handleToggleEnabled()}
                >
                  <span className="bodytext-13">
                    AI 生成機能（テキスト / 画像）を有効にする
                  </span>
                </Toggle>
                {isOverviewFetching && (
                  <span className="bodytext-11 u-text-light-gray">
                    更新中...
                  </span>
                )}
              </CardBody>
            </Card>
          </Section>

          {/* ===== セクション 1: AI モデル設定プリセット ===== */}
          <Section>
            <div className="u-row-between u-items-center u-w100">
              <h2 className="heading-13">1. AI モデル設定</h2>
              <Button
                size="small"
                theme="primary"
                variant="outlined"
                leftIcon="add"
                onClick={() => setSettingModal({ mode: "create" })}
              >
                プリセットを作成
              </Button>
            </div>
            <p className="bodytext-11 u-text-light-gray">
              プロバイダ・モデル・生成パラメータの設定一式です。全体で 1
              件だけ有効にできます。有効なプリセットが無い場合は Django admin の
              AI 設定が使われます。
            </p>
            <Card>
              {settingPresets.length === 0 ? (
                <p className="bodytext-13 u-text-light-gray u-text-center u-ptb16">
                  プリセットがありません。「プリセットを作成」から追加してください。
                </p>
              ) : (
                settingPresets.map((preset) => (
                  <PresetRow key={preset.id} $active={preset.is_active}>
                    <div className="u-col u-fill u-gap2">
                      <div className="u-row u-items-center u-gap8">
                        <span className="heading-13">{preset.name}</span>
                        {preset.is_active && (
                          <StatusBadge
                            label="アクティブ"
                            variant="chip"
                            color="blue"
                            size="small"
                            leftIcon="check_circle"
                          />
                        )}
                      </div>
                      <span className="bodytext-11 u-text-light-gray">
                        {AI_PROVIDER_LABELS[preset.provider] ?? preset.provider} /{" "}
                        {preset.model_name} / temperature {preset.temperature}{" "}
                        / max_tokens {preset.max_tokens}
                        {preset.has_api_key ? " / APIキー設定済み" : ""}
                      </span>
                    </div>
                    <RowActions>
                      <Button
                        size="small"
                        theme="secondary"
                        variant="text"
                        leftIcon="network_check"
                        disabled={testingPresetId !== null}
                        onClick={() => void handleSettingTest(preset)}
                      >
                        {testingPresetId === preset.id ? "テスト中…" : "接続テスト"}
                      </Button>
                      {preset.is_active ? (
                        <Button
                          size="small"
                          theme="tertiary"
                          variant="text"
                          onClick={() =>
                            void handleSettingActivate(preset, false)
                          }
                        >
                          解除
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          theme="primary"
                          variant="outlined"
                          onClick={() =>
                            void handleSettingActivate(preset, true)
                          }
                        >
                          有効にする
                        </Button>
                      )}
                      <IconButton
                        icon="edit"
                        size={20}
                        title="編集"
                        ariaLabel="プリセットを編集"
                        onClick={() =>
                          setSettingModal({ mode: "edit", preset })
                        }
                      />
                      <IconButton
                        icon="content_copy"
                        size={20}
                        title="複製"
                        ariaLabel="プリセットを複製"
                        onClick={() =>
                          setSettingModal({ mode: "duplicate", preset })
                        }
                      />
                      <IconButton
                        icon="delete"
                        size={20}
                        title={
                          preset.is_active
                            ? "アクティブなプリセットは削除できません"
                            : "削除"
                        }
                        ariaLabel="プリセットを削除"
                        onClick={() => handleSettingDelete(preset)}
                        disabled={preset.is_active}
                      />
                    </RowActions>
                  </PresetRow>
                ))
              )}
            </Card>
          </Section>

          {/* ===== セクション 2: テキスト生成シーン別プロンプト ===== */}
          <Section>
            <h2 className="heading-13">2. テキスト生成のシーン別プロンプト</h2>
            <p className="bodytext-11 u-text-light-gray">
              シーンごとにプロンプト案を複数保存し、シーン内で 1
              件だけ有効にできます。有効な案が無い場合は従来の AI シーン設定
              （Django admin）→ コードデフォルトの順で使われます。
            </p>
            <div className="u-col u-w100 u-gap8">
              {textScenes.map(renderSceneCard)}
            </div>
          </Section>

          {/* ===== セクション 3: 画像生成シーン別プロンプト ===== */}
          {imageScenes.length > 0 && (
            <Section>
              <h2 className="heading-13">3. 画像生成のシーン別プロンプト</h2>
              <p className="bodytext-11 u-text-light-gray">
                アバター / プロジェクトバナー / プロフィール作品サムネの AI
                画像生成（gpt-image-1）で使うプロンプトです。システムプロンプトが
                基本指示、ルールがスタイル・構図の制約に相当します。ユーザーの
                希望テキスト（任意）は最後に追記されます。
              </p>
              <div className="u-col u-w100 u-gap8">
                {imageScenes.map(renderSceneCard)}
              </div>
            </Section>
          )}
        </>
      )}

      {/* ===== モーダル ===== */}
      {settingModal && (
        <SettingPresetModal
          initial={settingModal.mode === "create" ? null : settingModal.preset}
          isDuplicate={settingModal.mode === "duplicate"}
          isSaving={isCreatingSetting || isUpdatingSetting}
          onSubmit={(body) => void handleSettingSubmit(body)}
          onClose={() => setSettingModal(null)}
        />
      )}
      {sceneModal && (
        <ScenePresetModal
          scene={sceneModal.scene}
          initial={sceneModal.preset}
          isSaving={isCreatingScene || isUpdatingScene}
          onSubmit={(body) => void handleSceneSubmit(body)}
          onClose={() => setSceneModal(null)}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.description}
          confirmLabel="削除"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </PageWrapper>
  );
};

export default AdminAiSettings;
